import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { utc } from "moment";
import { useAppSelector } from "hooks/redux";

import { StatusCode } from "api/protocols";
import { IUIActions } from "redux/actions/ui";
import { IFacilityActions } from "redux/actions/facility";

import { IRegister } from "../../Admin";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Page from "components/page/Page";
import Input from "components/form/input/Input";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { Select } from "components/select/index";
import TextField from "components/form/textField/TextField";
import { isNumeric } from "helpers/Helpers";

import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { CloseCashOut, CalculateExpectedCashOutValues } from "api/rpc/2024-04/facilityAdmin/facility/register/shift";
import CashCountSheet from "./CashCountSheet";

function isSameCalendarDay(first: Date, second: Date) {
  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
}

function isLocalMidnight(date: Date) {
  return (
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0
  );
}

/** DatePickerInput only carries a calendar day; restore a real close time for today's date. */
function resolveClosedAt(closedAt: Date) {
  const now = new Date();

  if (isSameCalendarDay(closedAt, now) && isLocalMidnight(closedAt)) {
    return now;
  }

  return closedAt;
}

function mergeSelectedDateWithTime(selectedDate: Date, timeSource: Date) {
  const merged = new Date(selectedDate);
  merged.setHours(
    timeSource.getHours(),
    timeSource.getMinutes(),
    timeSource.getSeconds(),
    timeSource.getMilliseconds(),
  );
  return merged;
}

interface ICashOutNewProps {
  uiActions: IUIActions;
  facilityActions: IFacilityActions;
}

interface IValidatedField {
  data: string;
  isValid: boolean;
  isDirty: boolean;
}

interface ICashOutNewState {
  closed_at: Date;
  depositCash: IValidatedField;
  depositCheck: IValidatedField;
  expectedDepositCheck: string;
  expectedDepositCash: string;
  depositBagNumber: string;
  notes: string;
  registerId: number;
  registers: IRegister[];
  cashCountOpen: boolean;
}

export default function CashOutNew(props: ICashOutNewProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const { uiActions, facilityActions } = props;
  const { facilityStore } = useAppSelector(store => store);

  const { Option } = Select;

  const [cashOutNewState, setCashOutNewState] = useState<ICashOutNewState>({
    closed_at: new Date(),
    depositCash: { data: "", isValid: false, isDirty: false },
    depositCheck: { data: "", isValid: false, isDirty: false },
    expectedDepositCheck: "0.00",
    expectedDepositCash: "0.00",
    depositBagNumber: "",
    notes: "",
    registerId: undefined,
    registers: [],
    cashCountOpen: false,
  });

  useEffect(() => {
    void loadRegisters();
  }, []);

  useEffect(() => {
    if (cashOutNewState.registerId && !facilityStore.teeSheetSettings?.register_blind_close) {
      void calculateExpectedCashOutValues();
    }
  }, [cashOutNewState.registerId]);

  async function calculateExpectedCashOutValues() {
    const calculateExpectedCashOutValuesResponse = await CalculateExpectedCashOutValues(
      { register_id: cashOutNewState.registerId },
      true,
    );

    if (calculateExpectedCashOutValuesResponse.status !== StatusCode.OK) {
      setCashOutNewState(prev => ({
        ...prev,
        expectedDepositCash: null,
        expectedDepositCheck: null,
      }));
      return;
    }

    setCashOutNewState(prev => ({
      ...prev,
      expectedDepositCash: String(calculateExpectedCashOutValuesResponse.data?.deposit_cash_expected),
      expectedDepositCheck: String(calculateExpectedCashOutValuesResponse.data?.deposit_check_expected),
    }));
  }

  useEffect(() => {
    const navigationState = history.location.state as { registerId?: number } | undefined;

    if (navigationState?.registerId) {
      setCashOutNewState(prevState => ({
        ...prevState,
        registerId: navigationState.registerId,
      }));
    }
  }, [history.location.state]);

  async function loadRegisters() {
    const getRegistersResponse = await GetRegisters({}, true);

    if (getRegistersResponse.status !== StatusCode.OK) {
      uiActions.showError(t("Failed to get registers"));
      return;
    }

    const navigationState = history.location.state as { registerId?: number } | undefined;

    const registerId =
      navigationState?.registerId ??
      (localStorage.getItem("register") === null
        ? getRegistersResponse.data?.[0]?.id
        : JSON.parse(localStorage.getItem("register")).id);

    setCashOutNewState(prevState => ({
      ...prevState,
      registerId: registerId,
      registers: getRegistersResponse.data,
    }));
  }

  function navigateToCashOuts() {
    history.push("/admin/settings/cash-out");
  }

  function handleValidatedInputChange(id: string, data: string, isValid: boolean) {
    setCashOutNewState(prev => ({
      ...prev,
      [id]: {
        data,
        isValid,
        isDirty: true,
      },
    }));
  }

  function handleDateChange(date: Date) {
    setCashOutNewState(prevState => {
      const now = new Date();
      const timeSource = isSameCalendarDay(date, now) ? now : prevState.closed_at;

      return {
        ...prevState,
        closed_at: mergeSelectedDateWithTime(date, timeSource),
      };
    });
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setCashOutNewState(prev => ({ ...prev, [id]: value }));
  }

  function handleRegisterChange(registerId: number) {
    setCashOutNewState(prev => ({ ...prev, registerId }));
  }

  function openCashCount() {
    setCashOutNewState(prev => ({ ...prev, cashCountOpen: true }));
  }

  function closeCashCount() {
    setCashOutNewState(prev => ({ ...prev, cashCountOpen: false }));
  }

  function applyCashCountTotal(total: number) {
    const formattedTotal = total.toFixed(2);
    setCashOutNewState(prev => ({
      ...prev,
      cashCountOpen: false,
      depositCash: {
        data: formattedTotal,
        isValid: isNumeric(formattedTotal),
        isDirty: true,
      },
    }));
  }

  async function createCashOut() {
    if (!cashOutNewState.depositCash.isValid || !cashOutNewState.depositCheck.isValid || !cashOutNewState.registerId) {
      setCashOutNewState(prev => ({
        ...prev,
        depositCash: { ...prev.depositCash, isDirty: true },
        depositCheck: { ...prev.depositCheck, isDirty: true },
      }));
    } else {
      const putCashOutResponse = await CloseCashOut(
        {
          closed_at: utc(resolveClosedAt(cashOutNewState.closed_at)).toDate(),
          deposit_cash: parseFloat(cashOutNewState.depositCash.data),
          deposit_check: parseFloat(cashOutNewState.depositCheck.data),
          deposit_bag_number: cashOutNewState.depositBagNumber,
          notes: cashOutNewState.notes,
          register_id: cashOutNewState.registerId,
        },
        true,
      );

      if (putCashOutResponse.status !== StatusCode.OK) {
        uiActions.showError(t("Failed to create new cash out"));
        return;
      }

      const storedRegister = JSON.parse(localStorage.getItem("register"));
      if (storedRegister && storedRegister.id === cashOutNewState.registerId) {
        localStorage.setItem("register", JSON.stringify({ ...storedRegister, active_shift: false }));
      }

      void facilityActions.loadRegisters(null, true);

      navigateToCashOuts();
    }
  }

  const primaryAction = {
    content: t("Create Cash Out"),
    action: createCashOut,
  };

  const secondaryActions = [
    {
      content: t("Count Cash"),
      action: openCashCount,
    },
  ];

  return (
    <Page
      title={t("New Cash Out")}
      narrow
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Cash Outs"),
          url: "/admin/settings/cash-out",
        },
      ]}
    >
      {!facilityStore.teeSheetSettings?.register_blind_close &&
      cashOutNewState.expectedDepositCash &&
      cashOutNewState.expectedDepositCheck ? (
        <Card>
          <Card.Section>
            <div className="flex items-center">
              <p className="text-medium text-gray-900 w-full">
                {t("Deposit Cash Expected")}:{" "}
                <span className="text-medium text-gray-600">${cashOutNewState.expectedDepositCash}</span>
              </p>
              <p className="text-medium text-gray-900 w-full" style={{ marginLeft: "2rem" }}>
                {t("Deposit Check Expected")}:{" "}
                <span className="text-medium text-gray-600">${cashOutNewState.expectedDepositCheck}</span>
              </p>
            </div>
          </Card.Section>
        </Card>
      ) : null}
      <Card>
        <Card.Section title={t("Cash Out")}>
          <FormLayout>
            <FormLayout.Group>
              <DatePickerInput
                months={1}
                position="left"
                startingDate={cashOutNewState.closed_at}
                setStartingDate={handleDateChange}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={cashOutNewState.depositCash.data}
                label={t("Deposit Cash")}
                onChange={(e: any) =>
                  handleValidatedInputChange("depositCash", String(e.target.value), isNumeric(String(e.target.value)))
                }
                error={!cashOutNewState.depositCash.isValid && cashOutNewState.depositCash.isDirty}
              />
              <Input
                value={cashOutNewState.depositCheck.data}
                label={t("Deposit Check")}
                onChange={(e: any) =>
                  handleValidatedInputChange("depositCheck", String(e.target.value), isNumeric(String(e.target.value)))
                }
                error={!cashOutNewState.depositCheck.isValid && cashOutNewState.depositCheck.isDirty}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={cashOutNewState.depositBagNumber}
                label={t("Deposit Bag Number")}
                id="depositBagNumber"
                onChange={handleInputChange}
              />
              <Select
                label={t("Register")}
                onChange={handleRegisterChange}
                defaultValue={cashOutNewState.registerId}
              >
                {cashOutNewState.registers?.map((register, index) => {
                  return (
                    <Option key={index} value={register.id}>
                      {register.title}
                    </Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField
                value={cashOutNewState.notes}
                label={t("Notes")}
                id="notes"
                onChange={handleInputChange}
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>

      <CashCountSheet
        open={cashOutNewState.cashCountOpen}
        currencyCode={facilityStore.facility?.default_currency_code}
        onCancel={closeCashCount}
        onApply={applyCashCountTotal}
      />
    </Page>
  );
}
